import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Clock, Play, Trash2, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Rutine: le adaugi de aici, fără programator.
 *
 * Frecvențele și zilele vin de la server, nu dintr-o listă scrisă în
 * JavaScript: a patra frecvență ar fi trebuit adăugată în două locuri, iar al
 * doilea ar fi rămas în urmă.
 */
export default function RutineSettings({ workspaceId }) {
  const [date, setDate] = useState(null);
  const [documente, setDocumente] = useState([]);
  const [nou, setNou] = useState({ nume: "", frecventa: "", ora: 8, minut: 0, zi: 0, prompt: "" });
  const [busy, setBusy] = useState(false);
  const [eroare, setEroare] = useState("");

  const incarca = useCallback(async () => {
    try {
      const { data } = await api.get("/rutine", { params: { workspace_id: workspaceId } });
      setDate(data);
      setNou((n) => ({ ...n, frecventa: n.frecventa || data.frecvente[0]?.cheie || "" }));
      setEroare("");
    } catch (e) {
      setEroare(e?.response?.data?.detail || "Nu am putut citi rutinele");
    }
  }, [workspaceId]);

  useEffect(() => { if (workspaceId) incarca(); }, [workspaceId, incarca]);

  useEffect(() => {
    if (!workspaceId) return;
    api.get("/rutine/documente", { params: { workspace_id: workspaceId } })
      .then((r) => setDocumente(r.data.documente || []))
      .catch(() => setDocumente([]));
  }, [workspaceId, date]);

  const adauga = async () => {
    setBusy(true);
    try {
      await api.post("/rutine", { workspace_id: workspaceId, ...nou });
      setNou((n) => ({ ...n, nume: "", prompt: "" }));
      toast.success("Rutina a fost adăugată");
      await incarca();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut adăuga rutina");
    } finally { setBusy(false); }
  };

  const ruleaza = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rutine/${id}/ruleaza`, null,
        { params: { workspace_id: workspaceId }, timeout: 300000 });
      toast.success(`Am scris ${data.cale}`);
      await incarca();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rutina nu a putut rula");
    } finally { setBusy(false); }
  };

  const sterge = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/rutine/${id}`, { params: { workspace_id: workspaceId } });
      await incarca();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut șterge");
    } finally { setBusy(false); }
  };

  if (!date && !eroare) return null;
  const saptamanal = date?.frecvente?.some((f) => f.cheie === nou.frecventa && f.cheie === "saptamanal");

  return (
    <section className="bg-surface border border-line/10 mt-6" data-testid="rutine-settings">
      <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
        <Clock className="w-4 h-4 text-brand" />
        <h3 className="font-bold tracking-tight">Rutine</h3>
        <span className="label-tiny ml-2">// RULEAZĂ SINGURE, SCRIU UN DOCUMENT DATAT</span>
      </div>

      <div className="p-5 space-y-4">
        {eroare && <p className="text-xs text-red-400" data-testid="rutine-eroare">{eroare}</p>}

        {date?.probleme?.length > 0 && (
          <div className="text-xs text-amber-400 flex items-start gap-2" data-testid="rutine-probleme">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{date.probleme.join(" · ")}</span>
          </div>
        )}

        {date?.rutine?.length === 0 && (
          <p className="text-xs text-fg/50" data-testid="rutine-goale">
            Nicio rutină încă. Adaugă una mai jos: rulează singură la ora ei și lasă un
            document datat.
          </p>
        )}

        {date?.rutine?.length > 0 && (
          <ul className="space-y-2" data-testid="rutine-lista">
            {date.rutine.map((r) => (
              <li key={r.id} className="flex items-start gap-3 text-sm border border-line/10 p-3">
                <div className="flex-1">
                  <span className="font-medium">{r.nume}</span>
                  <span className="text-fg/40 text-xs">
                    {" — "}{r.eticheta_frecventa}{r.zi ? `, ${r.zi}` : ""} la {r.ora}
                  </span>
                  <span className="block text-xs text-fg/50 mt-1">„{r.prompt}"</span>
                  <span className="block text-[11px] text-fg/30 mt-1">
                    {r.ultima_rulare ? `ultima rulare ${r.ultima_rulare.slice(0, 16).replace("T", " ")} · ` : ""}
                    acum: {r.acum}
                  </span>
                </div>
                <button type="button" onClick={() => ruleaza(r.id)} disabled={busy}
                        data-testid={`rutina-ruleaza-${r.id}`} aria-label={`Rulează ${r.nume}`}
                        className="text-fg/40 hover:text-brand disabled:opacity-40">
                  <Play className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => sterge(r.id)} disabled={busy}
                        data-testid={`rutina-sterge-${r.id}`} aria-label={`Șterge ${r.nume}`}
                        className="text-fg/30 hover:text-red-400 disabled:opacity-40">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="border-t border-line/10 pt-3 space-y-2">
          <div className="flex gap-2">
            <input data-testid="rutina-nume" className="input-flat text-sm flex-1"
                   value={nou.nume} aria-label="Numele rutinei"
                   onChange={(e) => setNou({ ...nou, nume: e.target.value })} />
            <select data-testid="rutina-frecventa" className="input-flat text-sm"
                    aria-label="Cât de des" value={nou.frecventa}
                    onChange={(e) => setNou({ ...nou, frecventa: e.target.value })}>
              {(date?.frecvente || []).map((f) => (
                <option key={f.cheie} value={f.cheie}>{f.eticheta}</option>
              ))}
            </select>
            {saptamanal && (
              <select data-testid="rutina-zi" className="input-flat text-sm" aria-label="În ce zi"
                      value={nou.zi} onChange={(e) => setNou({ ...nou, zi: Number(e.target.value) })}>
                {(date?.zile || []).map((z, i) => <option key={z} value={i}>{z}</option>)}
              </select>
            )}
            <input data-testid="rutina-ora" type="number" min="0" max="23" aria-label="La ce oră"
                   className="input-flat text-sm w-16" value={nou.ora}
                   onChange={(e) => setNou({ ...nou, ora: Number(e.target.value) })} />
          </div>
          <input data-testid="rutina-prompt" className="input-flat text-sm w-full"
                 aria-label="Ce să întrebe" value={nou.prompt}
                 onChange={(e) => setNou({ ...nou, prompt: e.target.value })} />
          <button type="button" data-testid="rutina-adauga" onClick={adauga}
                  disabled={busy || !nou.nume.trim() || !nou.prompt.trim()}
                  className="px-3 py-2 text-sm border border-brand/50 text-brand hover:bg-brand/10 disabled:opacity-40">
            Adaugă rutina
          </button>
        </div>

        {documente.length > 0 && (
          <div className="border-t border-line/10 pt-3" data-testid="rutine-documente">
            <p className="label-tiny mb-2">documente scrise</p>
            <ul className="space-y-1">
              {documente.slice(0, 10).map((d) => (
                <li key={d.id} className="text-xs text-fg/60 flex items-center gap-2">
                  <FileText className="w-3 h-3 flex-shrink-0 text-fg/30" />
                  <span className="mono">{d.cale}</span>
                  <span className="text-fg/30">{d.continut.length} caractere</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
